import { useState } from "react";
import { useUserContext } from "../../auth/userContextProvider/UserContextProvider";
import { useDotLegalPostGetQueryArray, useDotLegalQueryClient } from "@common/hooks/dotLegalReactQuery";
import { UserSelectableItem } from "../../user/User.types";
import { TaskStateEnum } from "../AnnualWheel.types";
import { useDotLegalSnackbar } from "@dotlegal/dotlegal-ui-components";
import { useTranslation } from "../../localization/useTranslation";
import { useCustomListExport } from "../../customLists/customListView/useCustomListExport";
import { usePageView } from "../../common/pageView/PageView.hooks";
import { FilterPage, SessionStorageSavedView } from "../../common/pageView/PageView.types";
import { useSavedViewQuery } from "../../common/components/savedViews/SavedViewsMenu.hooks";
import { SavedView } from "../../common/components/savedViews/SavedViewsMenu.types";
import useActivityDisplayFields from "./activityDisplayFields/ActivityDisplayFields.hooks";
import { ActivitiesFilterRequest, ActivitiesSearchResultViewModel, ActivitySearchItem } from "./ActivitiesSearch.types";
import { withResponsible } from "./Activities.utils";
import { activityFieldTypeByFieldId } from "./activityFieldTypeMapping";
import { transformActivitiesFilterToBackend } from "./ActivitiesFilter.hooks";
import { ListFilterOperator, SortDirection } from "../../contract/ContractFilter.types";
import { FilterOption } from "../../common/components/filterPopover/FilterPopover.types";
import { withoutFilterPresentationState } from "@common/components/filterBar/filterPinning.ts";
import { withFilterOperators } from "@common/components/filterBar/filterOperatorHydration.ts";
import { StatusTimelineConfig } from "./components/statusTimeline/StatusTimelineFieldConfig";
import { usePermissionsProvider } from "../../auth/usePermissionsProvider";
import { usePanes, useRefetchWhenPaneSaves } from "../../main/panes/usePanes";
import ActivityPane from "../activityPane/ActivityPane";
import TemplateActivityPane from "../templateActivityPane/TemplateActivityPane";
import { useStateSessionStorage } from "@common/hooks/useStateSessionStorage";

export type ActivityDisplayData = Array<string> | { fields: Array<string>; timelineConfig?: StatusTimelineConfig };

export function parseActivityDisplayData(raw: ActivityDisplayData | undefined): { fields: Array<string>; timelineConfig?: StatusTimelineConfig } {
    if (Array.isArray(raw)) return { fields: raw };
    if (raw && typeof raw === "object" && Array.isArray(raw.fields)) {
        return { fields: raw.fields, timelineConfig: raw.timelineConfig };
    }
    return { fields: [] };
}

export function buildActivityDisplayData(fields: Array<string>, timelineConfig: StatusTimelineConfig | null | undefined): ActivityDisplayData {
    if (timelineConfig && fields.includes("status-timeline")) {
        return { fields, timelineConfig };
    }
    return fields;
}

export function useActivitiesDataMapping() {
    const savedViewQuery = useSavedViewQuery(FilterPage.Activities);
    const {
        currentViewId,
        getFilterData,
        getDisplayData,
        setFilterData,
        setDisplayData,
        setView,
        hasFilterChanged,
        hasDisplayChanged: pageViewHasDisplayChanged,
        isSavedView,
        savedView,
        pinnedFilterIds,
        setPinnedFilterIds,
    } = usePageView(FilterPage.Activities, savedViewQuery.data);

    const filterData = getFilterData<ActivitiesFilterRequest>();
    // filterData keeps the pins and the filter operators so saving a view captures them; everything that talks to the server uses
    // this one, so pinning an already-empty filter, or changing its operator, does not change the query key and refetch the list.
    const queryFilterData = withoutFilterPresentationState(filterData);
    const { tableFields: rawTableFields } = useActivityDisplayFields();
    const rawDisplayData = getDisplayData<ActivityDisplayData>();
    const parsedDisplayData = parseActivityDisplayData(rawDisplayData);
    const { hasTagsAccess } = usePermissionsProvider();
    const displayFields = hasTagsAccess ? parsedDisplayData.fields : parsedDisplayData.fields.filter((f) => f !== "tags");
    const tableFields = hasTagsAccess ? rawTableFields : rawTableFields.filter((f) => f.id !== "tags");
    const timelineConfig = parsedDisplayData.timelineConfig;

    const { permissions, customerId } = useUserContext();
    // Kept outside the filter data, like on contracts, so a search is not saved with a view or marks it as changed
    const [searchText, setSearchText] = useStateSessionStorage<string>(`activities-search:${customerId}`, "");
    const searchedFilterData: ActivitiesFilterRequest = { ...queryFilterData, searchText: searchText || undefined };
    const queryClient = useDotLegalQueryClient();
    const snackBar = useDotLegalSnackbar();
    const { translateString } = useTranslation();
    const { exportActivitiesInBackground } = useCustomListExport();

    const [activityForDeletion, setActivityForDeletion] = useState<ActivitySearchItem | undefined>(undefined);
    const { open: openPane } = usePanes();

    const searchUrl = "/activities/search";
    const apiDisplayFields = displayFields.filter((f) => f !== "status-timeline");
    const hasTimeline = displayFields.includes("status-timeline");
    const activitiesQueryKey = [searchUrl, searchedFilterData, apiDisplayFields, hasTimeline ? timelineConfig : undefined];
    const { data, isLoading, refetch } = useDotLegalPostGetQueryArray<ActivitiesSearchResultViewModel>(
        activitiesQueryKey,
        searchUrl,
        {
            displayFields: apiDisplayFields,
            filter: searchedFilterData,
            timelineConfig: hasTimeline ? timelineConfig : undefined,
        },
        { enabled: !savedViewQuery.isLoading }
    );

    function refetchData() {
        refetch();
    }

    useRefetchWhenPaneSaves(refetchData);

    function openActivity(annualWheelActivityId: string | undefined) {
        openPane(ActivityPane, {
            mode: "edit",
            annualWheelActivityId,
            startDate: getStartDate(),
        });
    }

    function openTemplates() {
        openPane(TemplateActivityPane, { startDate: getStartDate() });
    }

    async function applyResponsibleToCachedActivities(activityId: string, responsible: UserSelectableItem) {
        // Cancelling first stops an in flight refetch, for instance one triggered by window focus, from replacing
        // the list with server data that predates the save.
        await queryClient.cancelQueriesByPredicate((q) => q.state.status === "success");

        // The updater form reads the latest cache rather than this render's data, so editing several rows in
        // quick succession cannot revert an earlier one.
        queryClient.setQueryDataArray<ActivitiesSearchResultViewModel | undefined>(
            activitiesQueryKey,
            (current) =>
                current && {
                    ...current,
                    activities: current.activities.map((activity) => (activity.id === activityId ? withResponsible(activity, responsible) : activity)),
                }
        );
    }

    const hasData = isLoading || savedViewQuery.isLoading || (data?.totalTaskCount ?? 0) > 0;

    function setSortOrder(column: string) {
        const temp = { ...filterData };
        const isCurrentColumn = column === temp.sortByColumn;
        temp.sortDirection = isCurrentColumn && temp.sortDirection === SortDirection.ASC ? SortDirection.DESC : SortDirection.ASC;

        if (!isCurrentColumn) {
            temp.sortByColumn = column;
        }

        setFilterData(temp);
    }

    function onBoxCounterClick(status: TaskStateEnum) {
        const currentValues = filterData?.status?.value ?? [];
        const statusStr = status.toString();
        const updatedValues = [...currentValues];

        const index = updatedValues.indexOf(statusStr);
        if (index > -1) {
            updatedValues.splice(index, 1);
        } else {
            updatedValues.push(statusStr);
        }

        setFilterData({
            ...filterData,
            status: updatedValues.length > 0 ? { value: updatedValues, filterOperator: ListFilterOperator.In } : undefined,
            pageNumber: 1,
        });
    }

    const onFilterBarUpdate = (filterOptions: Array<FilterOption>) => {
        setFilterData(
            withFilterOperators(transformActivitiesFilterToBackend(filterOptions, filterData?.sortByColumn, filterData?.sortDirection), filterOptions)
        );
    };

    const onSearchChange = (value: string) => {
        setSearchText(value);
        setFilterData({ ...filterData, pageNumber: 1 });
    };

    function getStartDate() {
        var date = new Date();
        return new Date(date.getFullYear(), date.getMonth(), date.getDate());
    }

    const onViewSaved = (viewId: string) => {
        const updatedView: SessionStorageSavedView = {
            page: FilterPage.Activities,
            viewId: viewId,
            filterData: filterData,
            displayData: rawDisplayData,
        };
        setView(updatedView);
    };

    const onViewDeleted = (deletedViewId: string) => {
        const fallbackView = savedViewQuery.data?.find((x) => x.isCustomerDefault || x.isSystemDefault);
        if (deletedViewId === currentViewId) {
            const updatedView: SessionStorageSavedView = {
                page: FilterPage.Activities,
                viewId: fallbackView?.id === deletedViewId ? undefined : fallbackView?.id,
                filterData: filterData,
                displayData: rawDisplayData,
            };
            setView(updatedView);
        }
    };

    const onChangeView = (view: SavedView) => {
        if (view.id !== currentViewId) {
            const updatedView: SessionStorageSavedView = {
                page: FilterPage.Activities,
                viewId: view.id,
                filterData: view.filterData,
                displayData: view.displayData,
            };
            setView(updatedView);
        }
    };

    async function onDownloadExcelReportClick() {
        const tableFieldsDict = Object.fromEntries(tableFields.map((f) => [f.id, f]));
        const existingColumns = displayFields.filter((x) => tableFieldsDict[x] !== undefined && x !== "status-timeline");
        const headers = existingColumns.map((x) => ({
            header: tableFieldsDict[x].name,
            fieldType: activityFieldTypeByFieldId[x],
        }));
        const hasTimeline = displayFields.includes("status-timeline");
        await exportActivitiesInBackground(savedView!.name, headers, {
            displayFields: existingColumns,
            filter: searchedFilterData,
            timelineConfig: hasTimeline ? timelineConfig : undefined,
        });
        snackBar.show(translateString("activityDataExportInProgress"), "success", true);
    }

    return {
        pinnedFilterIds,
        setPinnedFilterIds,
        isLoading: isLoading || savedViewQuery.isLoading,
        data,
        refetchData,
        openActivity,
        openTemplates,
        applyResponsibleToCachedActivities,
        permissions,
        onBoxCounterClick,
        hasData,
        getStartDate,
        activityForDeletion,
        setActivityForDeletion,
        onFilterBarUpdate,
        searchText,
        searchedFilterData,
        onSearchChange,
        setSortOrder,
        onViewSaved,
        onViewDeleted,
        onChangeView,
        currentViewId,
        filterData,
        displayFields,
        timelineConfig,
        rawDisplayData,
        savedViewQuery,
        savedView,
        isSavedView,
        hasFilterChanged,
        pageViewHasDisplayChanged,
        setDisplayData,
        setFilterData,
        tableFields,
        onDownloadExcelReportClick,
    };
}
